import { useEffect, useState } from 'react'

/**
 * Cookies / analytics consent notice.
 *
 * The site itself stores only three short strings in localStorage
 * (pb-theme, pb-lang, pb-analytics-consent) and no HTTP cookies of its own,
 * so that part is "strictly necessary" and needs no consent. GA4 also runs
 * cookieless (client_storage:'none' in index.html).
 *
 * Microsoft Clarity is different: it sets first-party tracking cookies
 * (_clck / _clsk) and records sessions, so it is CONSENT-GATED here. We do
 * not load Clarity until the visitor explicitly accepts; if they decline (or
 * before they choose) the site stays genuinely cookieless. The choice is
 * remembered in pb-analytics-consent ('granted' | 'denied') so the banner
 * shows once per browser.
 *
 * Defensive design: every localStorage call is wrapped in try/catch - Safari
 * Private Browsing and locked-down enterprise builds throw on storage writes,
 * and we don't want a thrown DOMException to break render or re-show the
 * banner every load.
 */
const CONSENT_KEY = 'pb-analytics-consent'
const CLARITY_ID = 'xrchcnb6j1'

function safeRead() {
  try {
    return localStorage.getItem(CONSENT_KEY)
  } catch {
    return null
  }
}

function safeWrite(value) {
  try {
    localStorage.setItem(CONSENT_KEY, value)
  } catch {
    /* storage blocked - accept silently; the banner reappears next visit */
  }
}

/**
 * Inject the Microsoft Clarity tag once. This is the vendor snippet, run only
 * after consent so no tracking cookie is set for visitors who don't opt in.
 */
function loadClarity() {
  if (typeof window === 'undefined') return
  if (window.clarity || document.getElementById('pb-clarity')) return
  ;(function (c, l, a, r, i) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) }
    const t = l.createElement(r)
    t.async = 1
    t.id = 'pb-clarity'
    t.src = 'https://www.clarity.ms/tag/' + i
    const y = l.getElementsByTagName(r)[0]
    y.parentNode.insertBefore(t, y)
  })(window, document, 'clarity', 'script', CLARITY_ID)
}

export default function CookieNotice() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const consent = safeRead()
    if (consent === 'granted') {
      // Returning visitor who already opted in: load Clarity, no banner.
      loadClarity()
      return
    }
    if (consent === 'denied') return // respect the prior decline, no banner
    // No choice yet - show the banner after the hero animation settles.
    const t = setTimeout(() => setVisible(true), 800)
    return () => clearTimeout(t)
  }, [])

  if (!visible) return null

  function accept() {
    safeWrite('granted')
    loadClarity()
    setVisible(false)
  }

  function decline() {
    safeWrite('denied')
    setVisible(false)
  }

  return (
    <div className="pb-cookie-notice" role="dialog" aria-label="Cookies notice" aria-live="polite">
      <p className="pb-cookie-notice-text">
        We store your theme and language as browser-side preferences. With your
        consent we also use Microsoft Clarity to understand how the site is
        used.{' '}
        <a href="#/cookies-policy">Cookies policy →</a>
      </p>
      <div className="pb-cookie-notice-actions">
        <button
          type="button"
          onClick={decline}
          className="pb-cookie-notice-btn pb-cookie-notice-btn--ghost"
          aria-label="Decline analytics cookies"
        >
          Decline
        </button>
        <button
          type="button"
          onClick={accept}
          className="pb-cookie-notice-btn"
          aria-label="Accept analytics cookies"
        >
          Accept
        </button>
      </div>
    </div>
  )
}
